import { Route } from '@angular/router';
import { FeatureKey, Roles } from '../../_models/feature';
import {
  CUSTOMER_CONTACTS_ROUTE,
  CUSTOMER_CONTACT_TABS
} from '../../kuba/contact/contact-tabs';
import {
  EDITOR_CONTACTS_ROUTE,
  editorContactTabs
} from '../../kuba/editorcontacts/editorcontact-tabs';
import {
  employeeListTabs,
  employeeTabLinkText,
  employeeTabRights
} from '../../kuba/employees/employee-tabs.rules';
import { followUpTabs } from '../../kuba/follow-ups/followups-tabs';
import { Module } from '../../kuba/follow-ups/models/deviation';
import { foodSafetyZoneTabs } from '../../kuba/foodsafety/foodsafety-tabs';
import {
  isNavigableFlexiTab,
  isTabAllowed,
  tabRouteCommands
} from '../../shared/ecpl-tab-navigation/tab-navigation.rules';

/** One link in a module's hover sub-navigation. */
export interface SubNavigationLink {
  /** Feature id of the tab; hard-coded tabs have none. */
  id?: number;
  translationKey: string;
  /** Router commands, resolved like the navigation's own module links. */
  commands: any[];
}

/** A Food Safety zone tab, linked once the zone is known. */
export interface ZoneTab {
  id: number;
  translationKey: string;
  linkText: string;
}

/** What the hover sub-navigation lists for a module. */
export type SubNavigation =
  | { kind: 'tabs'; links: SubNavigationLink[] }
  | { kind: 'manuals' }
  /**
   * Food Safety: its tabs belong to a zone. The panel lists the zones, or the
   * tabs of the only zone, as the zone list page opens a single zone directly.
   */
  | { kind: 'zones'; modulePath: string; tabs: ZoneTab[] };

/** Session state the rules depend on, passed in so the rules stay testable. */
export interface SubNavigationContext {
  readonly urlBase: string;
  readonly roleId: string;
  readonly userRole: string;
  readonly isRemote: boolean;
  hasRight(rightId: number): boolean;
}

/** With fewer tabs than this, the module link alone is enough. */
const MIN_TABS = 2;

/**
 * Modules admins get no panel for. In R mode the role is Editor, so the
 * entered business's panels still show.
 */
const NOT_FOR_ADMINS = [FeatureKey.EMPLOYEES, FeatureKey.MANUALS];

type MenuBuilder = (feature: any, context: SubNavigationContext, canOrderBht: boolean) => SubNavigation | null;

/**
 * The pages whose tabs the hover sub-navigation mirrors, matched on the feature
 * id and the exact link the navigation opens, because one feature id can open
 * different pages: the admin Employees item opens `employee-admin/list`, and its
 * children are not that page's tabs. A feature matching no entry gets no panel.
 * That covers Projects, FDV and similar modules, whose tabs only exist inside a
 * selected record, and Our Company, whose tabs are hard-coded in the wizard.
 */
const MODULE_PAGES: { featureId: number; link: string; build: MenuBuilder }[] = [
  {
    featureId: FeatureKey.FOLLOW_UP,
    link: 'follow-up',
    build: (feature, context) =>
      tabMenu(
        linked(followUpTabs(feature.children, context.roleId, context.isRemote)).map(tab =>
          toLink(context, 'follow-up', tab, tabRouteCommands(tab.linkText, Module.FOLLOWUPS))
        )
      )
  },
  {
    featureId: FeatureKey.EMPLOYEES,
    link: 'employee/list',
    build: (feature, context) => {
      const tabs = linked(employeeListTabs(feature.children, context.roleId));
      if (tabs.length === 0) {
        return null;
      }
      const rights = employeeTabRights(context.userRole, rightId =>
        context.hasRight(rightId)
      );
      return tabMenu(
        tabs
          .filter(tab => isTabAllowed(rights, tab.id))
          .map(tab =>
            toLink(
              context,
              'employee',
              tab,
              tabRouteCommands(employeeTabLinkText(tab, 0), 0, 'EMPLOYEE')
            )
          )
      );
    }
  },
  {
    featureId: FeatureKey.FLEXI,
    link: 'Flexi',
    build: (feature, context, canOrderBht) =>
      tabMenu(
        linked(feature.children)
          .filter(
            tab =>
              isNavigableFlexiTab(tab) && tab.id !== FeatureKey.DEVIATIONS &&
              (tab.id !== FeatureKey.FLEXI_SETTINGS || context.roleId === '1') &&
              tab.id !== FeatureKey.FLEXI_ACTION_POINTS &&
              (tab.id !== FeatureKey.FLEXI_BOOKING_PORTAL || canOrderBht)
          )
          .sort((left, right) =>
            Number(left.id === FeatureKey.FLEXI_BOOKING_PORTAL) -
            Number(right.id === FeatureKey.FLEXI_BOOKING_PORTAL)
          )
          .map(tab =>
            toLink(context, 'Flexi', tab, tabRouteCommands(tab.linkText, Module.FLEXI))
          )
      )
  },
  {
    featureId: FeatureKey.CONTACTS,
    link: `${EDITOR_CONTACTS_ROUTE}/list`,
    build: (feature, context) =>
      tabMenu(
        editorContactTabs(context.roleId).map(tab =>
          toLink(context, EDITOR_CONTACTS_ROUTE, tab, [tab.linkText])
        )
      )
  },
  {
    featureId: FeatureKey.CONTACTS,
    link: CUSTOMER_CONTACTS_ROUTE,
    build: (feature, context) =>
      tabMenu(
        CUSTOMER_CONTACT_TABS.map(tab =>
          toLink(context, CUSTOMER_CONTACTS_ROUTE, tab, [tab.linkText])
        )
      )
  },
  {
    featureId: FeatureKey.MANUALS,
    link: 'manual/list',
    build: () => ({ kind: 'manuals' })
  },
  {
    featureId: FeatureKey.FOOD_SAFETY,
    link: 'foodsafety/list',
    build: (feature, context) => {
      const tabs = linked(foodSafetyZoneTabs(feature.children)).map(
        (tab): ZoneTab => ({
          id: tab.id,
          translationKey: tab.translationKey,
          linkText: tab.linkText
        })
      );
      return tabs.length > 0
        ? { kind: 'zones', modulePath: `${context.urlBase}/foodsafety`, tabs }
        : null;
    }
  }
];

/**
 * The hover sub-navigation for a top-level navigation feature, or null when the
 * module has none. Only the feature's own children are read: child ids repeat
 * across modules.
 * @param feature Feature node as listed in the left navigation.
 * @param context Current session state.
 * @param canOrderBht Confirmed booking access for the business in context.urlBase.
 */
export function buildSubNavigation(
  feature: any,
  context: SubNavigationContext,
  canOrderBht = false
): SubNavigation | null {
  // The app marks an admin by role id (which navigation it loads) and by role
  // name (the admin/ URLs); either one counts.
  const isAdmin = +context.roleId === Roles.ADMIN || context.userRole === 'Admin';
  if (isAdmin && NOT_FOR_ADMINS.indexOf(feature?.id) > -1) {
    return null;
  }
  const link = `${feature?.linkText ?? ''}`.trim().replace(/^\/+|\/+$/g, '');
  const page = MODULE_PAGES.find(
    entry => entry.featureId === feature?.id && entry.link === link
  );
  if (feature?.id === FeatureKey.FLEXI &&
      (feature.checked === false || feature.access === false)) {
    return null;
  }
  return page ? page.build(feature, context, canOrderBht) : null;
}

/**
 * Links to a Food Safety zone's tabs, as the zone page builds them.
 * @param menu The Food Safety sub-navigation.
 * @param zoneId The zone.
 */
export function zoneTabLinks(
  menu: { modulePath: string; tabs: ZoneTab[] },
  zoneId: number
): SubNavigationLink[] {
  return menu.tabs.map(tab => {
    const [linkText, ...params] = tabRouteCommands(tab.linkText, zoneId);
    return {
      id: tab.id,
      translationKey: tab.translationKey,
      commands: [`${menu.modulePath}/details/${zoneId}/${linkText}`, ...params]
    };
  });
}

/**
 * Every URL shape the route table serves, as path segments, where `:name`
 * matches any segment. The `**` catch-all only renders the not-found page, so
 * shapes through it are left out.
 * @param routes The router configuration.
 */
export function routePatterns(routes: Route[], parent: string[] = []): string[][] {
  return routes.flatMap(route => {
    const pattern = parent.concat((route.path ?? '').split('/').filter(Boolean));
    if (pattern.indexOf('**') > -1) {
      return [];
    }
    return [pattern, ...routePatterns(route.children ?? [], pattern)];
  });
}

/**
 * Whether URL path segments, without matrix parameters, match a route shape.
 * @param segments Path segments of the URL.
 * @param patterns Shapes from `routePatterns`.
 */
export function matchesRoutePattern(segments: string[], patterns: string[][]): boolean {
  return patterns.some(
    pattern =>
      pattern.length === segments.length &&
      pattern.every((part, index) => part.startsWith(':') || part === segments[index])
  );
}

/**
 * Drops links that open no route, such as a feature whose page no longer
 * exists. A tab menu left with too few links is dropped as a whole.
 * @param menu The module's sub-navigation.
 * @param isRoutable Whether router commands open a configured route.
 */
export function keepRoutable(
  menu: SubNavigation | null,
  isRoutable: (commands: any[]) => boolean
): SubNavigation | null {
  if (menu?.kind === 'tabs') {
    return tabMenu(menu.links.filter(link => isRoutable(link.commands)));
  }
  if (menu?.kind === 'zones') {
    // Any zone id stands in: the route shape takes it as a parameter.
    const tabs = menu.tabs.filter(tab =>
      isRoutable(zoneTabLinks({ modulePath: menu.modulePath, tabs: [tab] }, 0)[0].commands)
    );
    return tabs.length > 0 ? { ...menu, tabs } : null;
  }
  return menu;
}

function tabMenu(links: SubNavigationLink[]): SubNavigation | null {
  return links.length >= MIN_TABS ? { kind: 'tabs', links } : null;
}

/** Tabs with a link to open; entries without one are not navigation targets. */
function linked(tabs: any[] | null | undefined): any[] {
  return (tabs ?? []).filter(tab => `${tab?.linkText ?? ''}`.trim() !== '');
}

/**
 * A panel link: the tab's commands, relative to the route of the page that
 * shows the tab.
 */
function toLink(
  context: SubNavigationContext,
  pageRoute: string,
  tab: { id?: number; translationKey: string },
  tabCommands: any[]
): SubNavigationLink {
  const [linkText, ...params] = tabCommands;
  return {
    id: tab.id,
    translationKey: tab.translationKey,
    commands: [`${context.urlBase}/${pageRoute}/${linkText}`, ...params]
  };
}
